/**
 * Die offenen Punkte EINES Arbeitsbereichs.
 *
 * WARUM DIESE KOMPONENTE EXISTIERT
 *
 * Bis V1 lagen alle Hinweise gebuendelt oben in der Uebersicht: „2
 * widersprüchliche Merkmale · 1 Merkmale außerhalb des Modells · 1 offene
 * Prüfhinweise · 2 offene Beschaffungsangaben". Wer ein Produkt ansehen
 * wollte, las zuerst dessen Verwaltungsaufgaben -- und wer eine davon
 * erledigen wollte, musste sie erst dorthin verfolgen, wo sie bearbeitbar
 * war.
 *
 * Jetzt gilt: WARNUNG DORT, WO SIE BEARBEITET WERDEN KANN. Der Server
 * bestimmt fuer jeden Punkt seinen Bereich; diese Komponente zeigt die Punkte
 * EINES Bereichs, und nur dort.
 *
 * Deshalb steht hier auch mehr als in der frueheren Chip-Zeile: Anzahl,
 * Art UND die betroffenen Einzelpunkte. In der kundensicheren Uebersicht war
 * „Mindestabnahme" ein Beschaffungsbegriff vor einem Kunden; im Einkauf ist
 * es die Angabe, die jemand nachtragen soll.
 */
import type { AttentionItem, WorkSectionCode } from "@homebase/shared";
import type { I18n } from "../i18n/index.tsx";

const KONFLIKT = "▲";
const LUECKE = "⬦";

/**
 * Die Beschriftung eines einzelnen Punktes.
 *
 * Der Server liefert sprachneutrale Codes -- Merkmale, Pruefhinweisarten und
 * Beschaffungsfelder gemischt. Welches Woerterbuch zustaendig ist, entscheidet
 * sich erst hier; gefunden wird das erste, das den Code kennt.
 */
function itemLabel(i18n: I18n, code: string): string {
  for (const prefix of ["attnItem", "flag", "prop"]) {
    const label = i18n.tCode(prefix, code, "");
    if (label !== "") return label;
  }
  return code;
}

/**
 * Zu einer Merkmalszeile springen.
 *
 * `scrollIntoView` allein landet unter der Sprungleiste -- die Zeile waere da,
 * aber nicht zu sehen. Die Akte fuehrt ihre gemessene Kopfhoehe in
 * `--akte-kopfhoehe`; `scroll-margin-top` der Zielzeile nimmt sie auf, und
 * derselbe Wert traegt hier.
 *
 * Findet sich das Ziel nicht -- etwa weil der Abschnitt eingeklappt ist --
 * passiert nichts. Kein Sprung ins Leere und keine Fehlermeldung ueber eine
 * Kennung, die den Benutzer nichts angeht.
 */
function springeZu(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  /* Kurz hervorheben: nach einem Sprung ueber zwei Bildschirme ist sonst
     nicht zu sehen, WELCHE der gleichfoermigen Zeilen gemeint war. */
  el.classList.add("props__item--gesucht");
  window.setTimeout(() => el.classList.remove("props__item--gesucht"), 2000);
}

export function SectionAttention({ items, section, i18n }: {
  items: readonly AttentionItem[];
  section: WorkSectionCode;
  i18n: I18n;
}): React.JSX.Element | null {
  const eigene = items.filter((x) => x.section === section);
  // Kein Handlungsbedarf, keine Zone. Es gibt bewusst keine Entwarnungsbox:
  // eine Flaeche, die immer da ist, wird nicht mehr gelesen.
  if (eigene.length === 0) return null;

  return (
    <section className="sattn" aria-label={i18n.t("attn.aria")}>
      {eigene.map((it) => {
        const kopf = i18n.tCode("attn", it.code, it.code);
        const einzeln = it.items.map((x) => itemLabel(i18n, x));
        /*
         * Die Einzelpunkte stehen nur da, wenn sie etwas hinzufuegen. Bei
         * „widersprüchliche Merkmale: Lichtstrom · Leistung" tun sie es; bei
         * „Mindestabnahme fehlt: Mindestabnahme" waere es dieselbe Auskunft
         * zweimal untereinander.
         */
        const zeigen = einzeln.length > 1
          || (einzeln.length === 1 && !kopf.includes(einzeln[0]!));
        return (
          <div className={`sattn__item sattn__item--${it.kind}`} key={it.code}>
            <span className="sattn__mark" aria-hidden="true">
              {it.kind === "conflict" ? KONFLIKT : LUECKE}
            </span>
            <div className="sattn__body">
              <div className="sattn__head">{kopf}</div>
              {/*
                * DIE EINZELPUNKTE SIND WEGE, NICHT NUR WOERTER.
                *
                * „Merkmale ausserhalb des Modells: Farbwiedergabewert" sagte
                * bisher, DASS etwas ist -- und liess den Leser suchen. Betrifft
                * ein Punkt ein Merkmal, fuehrt ein Klick jetzt zu seiner Zeile,
                * wo der Grund ausformuliert steht.
                *
                * Punkte ohne Zielzeile (etwa „Zolltarifnummer fehlt") bleiben
                * Text: ein Knopf, der nirgendwohin fuehrt, ist schlimmer als
                * gar keiner.
                */}
              {zeigen && (
                <div className="sattn__items">
                  {it.items.map((code, i) => {
                    const ziel = it.section === "technik" ? `merkmal-${code}` : null;
                    return (
                      <span key={code}>
                        {i > 0 && " · "}
                        {ziel
                          ? <button type="button" className="sattn__sprung"
                              onClick={() => springeZu(ziel)}>{itemLabel(i18n, code)}</button>
                          : itemLabel(i18n, code)}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
